import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Trash,
  CheckCircle,
  XCircle,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Cross,
  X,
  Banknote,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "./ProductLoanReport.css";

const API_URL = "https://payyug.in/api/";

const ProductLoanReport = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    loanId: null,
    type: "",
  });

  const [firstEmiDate,setFirstEmiDate]=useState(new Date().toISOString().split("T")[0])

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");

  const superAdminId = localStorage.getItem("adminuserId");
  const fetchLoans = useCallback(async () => {
    if (!superAdminId) {
      toast.error("Session expired. Please login again.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}product-loan/report`, {
        params: {
          superAdminId,
          page: currentPage,
          limit: itemsPerPage,
        },
      });

      if (response.data.success) {
        setLoans(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to sync with server.");
    } finally {
      setLoading(false);
    }
  }, [superAdminId, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);


    const handleViewImage = (imagePath) => {
    if (!imagePath) return toast.info("Bill image is not Available");
    setSelectedImageUrl(`https://payyug.in/${imagePath}`);
    setShowImageModal(true);
  };

  const filteredLoans = loans.filter((loan) => {
    const matchesSearch =
      loan.formNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.ref1Name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter 
      ? loan.status?.toLowerCase() === statusFilter.toLowerCase() 
      : true;

    return matchesSearch && matchesStatus;
  });


  const openModal = (id, type) => setModalConfig({ isOpen: true, loanId: id, type });
  const closeModal = () => setModalConfig({ isOpen: false, loanId: null, type: "" });

const confirmAction = async () => {
  const { loanId, type } = modalConfig;
  
  try {
    const endpoint = type === "delete" ? `product-loan/delete-permanent/${loanId}` : `product-loan/${type}`;

    const payload = {
      superAdminId,
      loanId,
      ...(type === "disburse" && { startDate: firstEmiDate })
    };

    let res;
    if (type === "delete") {
      res = await axios.delete(`${API_URL}${endpoint}`);
    } else {
      res = await axios.post(`${API_URL}${endpoint}`, payload);
    }
    
    toast.success(res.data.message || `Loan ${type}ed successfully!`);
    fetchLoans();
  } catch (err) {
    toast.error(err?.response?.data?.message || `Failed to ${type} loan`);
  } finally {
    closeModal();
  }
};

  return (
    <div className="product-loan-report-container">
      <header className="product-loan-report-header">
        <h1 className="product-loan-report-main-title">Product Loan Report</h1>
      </header>

      <section className="product-loan-report-toolbar">
        <div className="product-loan-report-search-container">
          <input
            className="product-loan-report-search-input"
            type="text"
            placeholder="Search by Form No or Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="product-loan-report-filter-box">
          <Filter size={18} />
          <select
            className="product-loan-report-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="disbursed">Disbursed</option>
          </select>

          <select
            className="product-loan-report-filter-select"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">Show 10</option>
            <option value="25">Show 25</option>
            <option value="50">Show 50</option>
          </select>
        </div>
      </section>

      <div className="product-loan-report-table-wrapper">
        <table className="product-loan-report-table">
          <thead>
            <tr>
              <th>S.N.</th>
                <th>Bill Image</th>
                <th>Account No</th>
                <th>Customer Id</th>
                <th>Customer Name</th>
                <th>Vendor Price</th>
                <th>Down Payment</th>
                <th>Loan Amount</th>
                <th>Roi</th>
                <th>Tenure</th>
                <th>Emi Amount</th>
                <th>Total Payable</th>
                <th>Status</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="14" className="product-loan-report-loading">
                  <Loader2 className="product-loan-report-loading-spinner" /> Loading...
                </td>
              </tr>
            ) : filteredLoans.length > 0 ? (
              filteredLoans.map((loan,index) => (
                <tr key={loan.id} className="product-loan-report-table-row">
                  <td>{(currentPage - 1) * 10 + index + 1}</td>
                  <td>
                        <button
                          className="product-loan-report-image-view-btn"
                          title="View Bill"
                          onClick={() => handleViewImage(loan.billImage)}
                        >
                          <Eye />
                        </button>
                      </td>
                  <td className="product-loan-report-bold-text">{loan.formNo||"N/A"}</td>
                  <td className="product-loan-report-bold-text">{loan?.customer?.userId||"N/A"}</td>
                  <td className="product-loan-report-bold-text">{loan?.customer?.fullName||"N/A"}</td>
                  <td>{`₹${loan.vendorPrice?.toLocaleString()}`||"N/A"}</td>
                  <td>{`₹${loan.downPayment?.toLocaleString()}`||"N/A"}</td>
                  <td>{`₹${loan.loanAmount?.toLocaleString()}`||"N/A"}</td>
                  <td>{loan.roi||"N/A"}</td>
                      <td>{loan.term||"N/A"}</td>
                      <td>{`₹${loan.emiAmount?.toLocaleString()}`||"N/A"}</td>
                      <td>{`₹${loan.totalPayable?.toLocaleString()}`||"N/A"}</td>
                  <td>

                    <span className={`product-loan-report-table-badge product-loan-report-table-badge-${loan.status?.toLowerCase()}`}>
                      {loan.status}
                    </span>
                  </td>
                      
                  <td className="product-loan-report-table-actions">
                    {loan.status?.toLowerCase() === "pending" && (
                      <>
                        <button className="product-loan-report-table-btn product-loan-report-table-btn-approve" onClick={() => openModal(loan.id, "approve")}><CheckCircle size={18}  /> Approve</button>
                      </>
                    )}
                    {loan.status?.toLowerCase() === "approved" && (
                      <>
                        <button className="product-loan-report-table-btn product-loan-report-table-btn-approve" onClick={() => openModal(loan.id, "disburse")}><CheckCircle size={18} /> Disburse</button>
                      </>
                    )}
                    {loan.status?.toLowerCase() === "disbursed" && (
                      <>
                      <button className="product-loan-report-table-btn product-loan-report-table-btn-delete" disabled>Disbursed</button>
                      </>
                    )}
                    <button className="product-loan-report-table-btn product-loan-report-table-btn-delete" onClick={() => openModal(loan.id, "delete")}><Trash size={18} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="loan-pre-closer-empty">No records found on this page.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="product-loan-report-pagination">
        <button
          className="product-loan-report-page-btn"
          disabled={currentPage === 1 || loading}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="product-loan-report-page-info">
          Page <b>{currentPage}</b> of <b>{totalPages}</b>
        </span>
        <button
          className="product-loan-report-page-btn"
          disabled={currentPage >= totalPages || loading}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          <ChevronRight size={18} />
        </button>
      </footer>

      {/* --- ACTION MODAL --- */}
      {modalConfig.isOpen && (
        <div className="product-loan-report-modal-overlay">
          <div className="product-loan-report-modal-content">
            <div className={`product-loan-report-modal-icon pl-icon-${modalConfig.type}`}>
              {modalConfig.type === "delete" && <Trash size={40} />}
              {modalConfig.type === "approve" && <CheckCircle size={40} />}
              {modalConfig.type === "disburse" && <Banknote size={40} />}
            </div>
            <h2>
              Confirm{" "}
              {modalConfig.type.charAt(0).toUpperCase() +
                modalConfig.type.slice(1)}
            </h2>
            <p>
              Are you sure you want to <b>{modalConfig.type}</b> this loan
              record? This action might be irreversible.
            </p>

            {
              modalConfig.type==="disburse" && (
                <div className="disburse-first-emi-date">
                <label>First Emi Date</label>
                <input type="date" value={firstEmiDate} onChange={(e)=>setFirstEmiDate(e.target.value)}   />
                </div>
              )
            }

            <div className="product-loan-report-modal-actions">
              <button className="product-loan-report-table-btn-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button
                className={`product-loan-report-table-btn-confirm pl-btn-${modalConfig.type}`}
                onClick={confirmAction}
              >
                Confirm {modalConfig.type}
              </button>
            </div>
          </div>
        </div>
      )}


      {showImageModal && (
        <div
          className="product-loan-report-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="product-loan-report-image-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="product-loan-report-modal-close-btn"
              onClick={() => setShowImageModal(false)}
            >
              <X style={{marginLeft:"-10px"}}/>
            </button>
            <div className="product-loan-report-image-modal-container">
              <img
                src={selectedImageUrl}
                alt="Bill"
                className="product-loan-report-image-modal-full"
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/400?text=Image+Not+Found";
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ProductLoanReport
